import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Server, Inbox, Send, Loader2, CheckCircle2, XCircle, Plug, FolderTree, Repeat, CalendarRange, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const empty = { host: "", port: 993, email: "", password: "", ssl: true, authMethod: "password" };

function MailboxCard({ side, data, setData, providers }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [useAppPassword, setUseAppPassword] = useState(false);
  const isSource = side === "source";
  const selectedProvider = providers.find((p) => p.id === data.provider);
  // Only Gmail supports an App Password fallback — Microsoft disabled Basic Auth
  // for IMAP entirely in Oct 2022, so Office 365 stays OAuth-only.
  const canUseAppPassword = data.provider === "gmail";
  const needsOAuth = !!selectedProvider?.oauth && !(canUseAppPassword && useAppPassword);

  const formatSize = (bytes) => {
    if (bytes == null) return null;
    const mb = bytes / (1024 * 1024);
    return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
  };

  const fetchOverview = async (creds) => {
    setOverviewLoading(true);
    setOverview(null);
    try {
      const { data: r } = await api.post("/mailbox-overview", creds);
      if (r.ok) setOverview(r);
    } catch (err) {
      // non-fatal — overview is a nice-to-have, don't block the flow on failure
    } finally {
      setOverviewLoading(false);
    }
  };

  const applyProvider = (id) => {
    const p = providers.find((x) => x.id === id);
    if (!p) return;
    setData((d) => ({
      ...d, host: p.host, port: p.port, ssl: p.ssl, provider: id, note: p.note,
      authMethod: p.oauth ? "oauth" : "password",
      // reset any previous connection/credentials when switching providers
      accessToken: null, refreshToken: null, password: "",
    }));
    setUseAppPassword(false);
    setOverview(null);
  };

  const connectOAuth = () => {
    const provider = data.provider === "gmail" ? "google" : "microsoft";
    setConnecting(true);
    api.get(`/oauth/mail/${provider}/authorize`).then(({ data: r }) => {
      const popup = window.open(r.url, "mailshift-oauth", "width=520,height=650");
      const onMessage = (event) => {
        const payload = event.data;
        if (!payload || payload.source !== "mailshift-oauth") return;
        window.removeEventListener("message", onMessage);
        setConnecting(false);
        if (payload.status === "connected") {
          setData((d) => ({
            ...d, email: payload.email, accessToken: payload.access_token,
            refreshToken: payload.refresh_token, oauthProvider: provider,
          }));
          toast.success(`Connected: ${payload.email}`);
          fetchOverview({
            host: data.host, port: Number(data.port), email: payload.email,
            ssl: data.ssl, auth_method: "oauth", access_token: payload.access_token,
          });
        } else {
          toast.error(payload.error || "Could not connect account");
        }
      };
      window.addEventListener("message", onMessage);
    }).catch((err) => {
      setConnecting(false);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    });
  };

  const testConn = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data: r } = needsOAuth
        ? await api.post("/test-connection", {
            host: data.host, port: Number(data.port), email: data.email,
            ssl: data.ssl, auth_method: "oauth", access_token: data.accessToken,
          })
        : await api.post("/test-connection", {
            host: data.host, port: Number(data.port), email: data.email,
            password: data.password, ssl: data.ssl,
          });
      setResult(r);
      r.ok ? toast.success(`${isSource ? "Source" : "Destination"}: ${r.message} (${r.folder_count} folders)`)
           : toast.error(r.message);
      if (r.ok && !needsOAuth) {
        fetchOverview({
          host: data.host, port: Number(data.port), email: data.email,
          password: data.password, ssl: data.ssl,
        });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-6 py-4">
        {isSource ? <Inbox className="h-4 w-4 text-primary" /> : <Send className="h-4 w-4 text-emerald-600" />}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{isSource ? "Source" : "Destination"}</p>
          <p className="font-heading font-bold tracking-tight">{isSource ? "Migrate From" : "Migrate To"}</p>
        </div>
      </div>

      <div className="space-y-4 p-6">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em]">Provider preset</Label>
          <Select onValueChange={applyProvider}>
            <SelectTrigger data-testid={`${side}-provider-select`}><SelectValue placeholder="Select a provider" /></SelectTrigger>
            <SelectContent>
              {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {data.note ? <p className="text-xs text-muted-foreground">{data.note}</p> : null}
          {canUseAppPassword && (
            <button
              type="button"
              className="text-xs font-medium text-primary underline underline-offset-2"
              onClick={() => {
                const goingToAppPassword = !useAppPassword;
                setUseAppPassword(goingToAppPassword);
                setData((d) => ({
                  ...d, accessToken: null, refreshToken: null, password: "",
                  authMethod: goingToAppPassword ? "password" : "oauth",
                }));
                setOverview(null);
              }}
              data-testid={`${side}-toggle-app-password`}
            >
              {useAppPassword ? "Use Google sign-in (OAuth) instead" : "Or use an App Password instead"}
            </button>
          )}
        </div>

        {canUseAppPassword && useAppPassword && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Requires 2-Step Verification to be turned on for the Google account.{" "}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">
              Generate an App Password →
            </a>
          </p>
        )}

        {needsOAuth ? (
          <div className="space-y-3">
            {data.accessToken ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Connected as <span className="font-mono">{data.email}</span>
                </div>
                <button
                  type="button"
                  className="text-xs font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                  onClick={() => { setData((d) => ({ ...d, accessToken: null, refreshToken: null, email: "", oauthProvider: null })); setOverview(null); }}
                  data-testid={`${side}-oauth-disconnect-button`}
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="w-full gap-2" onClick={connectOAuth}
                      disabled={connecting} data-testid={`${side}-oauth-connect-button`}>
                {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Connect with {data.provider === "gmail" ? "Google" : "Microsoft"}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em]">IMAP Host</Label>
                <Input value={data.host} onChange={(e) => setData((d) => ({ ...d, host: e.target.value }))}
                       placeholder="imap.example.com" data-testid={`${side}-host-input`} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em]">Port</Label>
                <Input type="number" value={data.port} onChange={(e) => setData((d) => ({ ...d, port: e.target.value }))}
                       data-testid={`${side}-port-input`} className="font-mono" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em]">Email / Username</Label>
              <Input value={data.email} onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))}
                     placeholder="user@example.com" data-testid={`${side}-email-input`} className="font-mono" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em]">Password / App Password</Label>
              <Input type="password" value={data.password} onChange={(e) => setData((d) => ({ ...d, password: e.target.value }))}
                     data-testid={`${side}-password-input`} />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Use SSL/TLS</span>
              </div>
              <Switch checked={data.ssl} onCheckedChange={(v) => setData((d) => ({ ...d, ssl: v }))}
                      data-testid={`${side}-ssl-switch`} />
            </div>
          </>
        )}

        <Button variant="outline" className="w-full gap-2" onClick={testConn}
                disabled={testing || !data.host || !data.email || (needsOAuth && !data.accessToken)}
                data-testid={`${side}-test-button`}>
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
          Test Connection
        </Button>
        {result && (
          <div className={`flex items-center gap-2 text-sm ${result.ok ? "text-emerald-600" : "text-red-600"}`}>
            {result.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            <span className="font-mono text-xs">{result.message}</span>
          </div>
        )}
        {(overviewLoading || overview) && (
          <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-xs" data-testid={`${side}-mailbox-overview`}>
            {overviewLoading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Checking mailbox size…
              </span>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                <span><span className="font-medium text-foreground">{overview.messages.toLocaleString()}</span> messages</span>
                <span><span className="font-medium text-foreground">{overview.folders}</span> folders</span>
                <span>
                  <span className="font-medium text-foreground">
                    {formatSize(overview.size_bytes) || "size unavailable"}
                  </span>
                  {overview.size_bytes == null && " (server doesn't report size)"}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewMigration() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [name, setName] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [source, setSource] = useState({ ...empty });
  const [dest, setDest] = useState({ ...empty });
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState({});
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [recurring, setRecurring] = useState(false);
  const [recurMins, setRecurMins] = useState(1440);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFolders = async () => {
    if (!source.host || !source.email) { toast.error("Fill source mailbox first"); return; }
    if (source.authMethod === "oauth" && !source.accessToken) { toast.error("Connect the source account first"); return; }
    setLoadingFolders(true);
    try {
      const { data } = source.authMethod === "oauth"
        ? await api.post("/list-folders", {
            host: source.host, port: Number(source.port), email: source.email,
            ssl: source.ssl, auth_method: "oauth", access_token: source.accessToken,
          })
        : await api.post("/list-folders", {
            host: source.host, port: Number(source.port), email: source.email,
            password: source.password, ssl: source.ssl,
          });
      if (!data.ok) { toast.error(data.message || "Could not list folders"); return; }
      setFolders(data.folders);
      const all = {};
      data.folders.forEach((f) => (all[f] = true));
      setSelected(all);
      toast.success(`Loaded ${data.folders.length} folder(s)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingFolders(false);
    }
  };

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  const submit = async (startNow) => {
    if (!name || !source.host || !source.email || !dest.host || !dest.email) {
      toast.error("Please fill migration name and both mailbox details");
      return;
    }
    if (source.authMethod === "oauth" && !source.refreshToken) {
      toast.error("Connect the source account first"); return;
    }
    if (dest.authMethod === "oauth" && !dest.refreshToken) {
      toast.error("Connect the destination account first"); return;
    }
    setSaving(true);
    try {
      const chosen = folders.filter((f) => selected[f]);
      if (dateFrom && dateTo && dateFrom > dateTo) {
        toast.error("Date range: 'From' cannot be after 'To'");
        setSaving(false);
        return;
      }
      const payload = {
        name,
        source_host: source.host, source_port: Number(source.port), source_email: source.email,
        source_ssl: source.ssl,
        source_auth_method: source.authMethod === "oauth" ? "oauth" : "password",
        source_password: source.authMethod === "oauth" ? null : source.password,
        source_oauth_provider: source.authMethod === "oauth" ? source.oauthProvider : null,
        source_refresh_token: source.authMethod === "oauth" ? source.refreshToken : null,
        dest_host: dest.host, dest_port: Number(dest.port), dest_email: dest.email,
        dest_ssl: dest.ssl,
        dest_auth_method: dest.authMethod === "oauth" ? "oauth" : "password",
        dest_password: dest.authMethod === "oauth" ? null : dest.password,
        dest_oauth_provider: dest.authMethod === "oauth" ? dest.oauthProvider : null,
        dest_refresh_token: dest.authMethod === "oauth" ? dest.refreshToken : null,
        scheduled_at: scheduleAt ? new Date(scheduleAt).toISOString() : null,
        selected_folders: (folders.length && chosen.length < folders.length) ? chosen : null,
        recurring: recurring,
        recurring_minutes: recurring ? Number(recurMins) : null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      };
      const { data } = await api.post("/migrations", payload);
      if (scheduleAt) {
        toast.success("Migration scheduled");
      } else if (startNow) {
        await api.post(`/migrations/${data.id}/start`);
        toast.success("Migration queued");
      } else {
        toast.success("Migration saved as draft");
      }
      navigate(`/migrations/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Create</p>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">New Migration</h1>
      </div>

      <div className="mb-6 border border-border bg-card p-6">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em]">Migration name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. John's mailbox → new server" data-testid="migration-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em]">Schedule for later (optional)</Label>
            <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                   data-testid="migration-schedule-input" />
            <p className="text-xs text-muted-foreground">Leave empty to run immediately. Set a time to run overnight automatically.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <MailboxCard side="source" data={source} setData={setSource} providers={providers} />
        <MailboxCard side="destination" data={dest} setData={setDest} providers={providers} />
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderTree className="h-4 w-4 text-primary" />
              <span className="font-heading font-bold tracking-tight">Folders</span>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={loadFolders} disabled={loadingFolders} data-testid="load-folders-button">
              {loadingFolders ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderTree className="h-4 w-4" />}
              Load source folders
            </Button>
          </div>
          {folders.length === 0 ? (
            <p className="text-sm text-muted-foreground">Load folders to pick which ones to migrate. By default all folders are migrated (Inbox→Inbox, Sent→Sent, no Gmail labels).</p>
          ) : (
            <div className="max-h-56 space-y-2 overflow-y-auto">
              {folders.map((f) => (
                <label key={f} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox checked={!!selected[f]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [f]: !!v }))} data-testid={`folder-check-${f}`} />
                  <span className="font-mono">{f}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <Repeat className="h-4 w-4 text-primary" />
            <span className="font-heading font-bold tracking-tight">Recurring Sync</span>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
            <span className="text-sm">Auto re-sync on a repeating schedule</span>
            <Switch checked={recurring} onCheckedChange={setRecurring} data-testid="recurring-switch" />
          </div>
          {recurring && (
            <div className="mt-4 space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em]">Every (minutes)</Label>
              <Input type="number" min={5} value={recurMins} onChange={(e) => setRecurMins(e.target.value)} data-testid="recurring-minutes-input" className="font-mono" />
              <p className="text-xs text-muted-foreground">e.g. 60 = hourly, 1440 = daily. Only new emails are copied each run.</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-primary" />
          <span className="font-heading font-bold tracking-tight">Date Range (optional)</span>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Migrate only emails within a specific date range. Leave both empty to migrate all emails.
        </p>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em]">From (on or after)</Label>
            <Input type="date" value={dateFrom} max={dateTo || undefined}
                   onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em]">To (on or before)</Label>
            <Input type="date" value={dateTo} min={dateFrom || undefined}
                   onChange={(e) => setDateTo(e.target.value)} data-testid="date-to-input" />
          </div>
        </div>
        {(dateFrom || dateTo) && (
          <p className="mt-3 text-xs text-primary" data-testid="date-range-summary">
            Only emails {dateFrom ? `from ${dateFrom}` : "up"} {dateTo ? `to ${dateTo}` : "onward"} will be migrated.
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={() => submit(false)} disabled={saving} data-testid="save-migration-button">
          Save as draft
        </Button>
        <Button className="gap-2" onClick={() => submit(true)} disabled={saving} data-testid="start-migration-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {scheduleAt ? "Schedule Migration" : "Save & Start Migration"}
        </Button>
      </div>
    </div>
  );
}
